import { useRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/** Eye / eye-off toggle with a 44 px touch target and an accessible label. */
export const RevealButton = ({ shown, onToggle, label = "password", className, testid }) => (
  <button type="button" onClick={onToggle} aria-label={shown ? `Hide ${label}` : `Show ${label}`} aria-pressed={shown}
    className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500", className)} data-testid={testid}>
    {shown ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
  </button>
);

/** Password input with a show/hide toggle inside the field. */
export const SecretInput = ({ value, onChange, shown, onToggle, className, testid, label = "password", ...p }) => (
  <div className="relative">
    <Input type={shown ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} className={cn("pr-12 text-base", className)} data-testid={testid} {...p} />
    <RevealButton shown={shown} onToggle={onToggle} label={label} className="absolute right-0.5 top-1/2 -translate-y-1/2" testid={testid && `${testid}-toggle`} />
  </div>
);

/** n single-digit boxes: auto-advance, backspace goes back, paste fills all; onComplete fires on the last digit. */
export const DigitBoxes = ({ n = 4, value, onChange, onComplete, shown, label = "PIN", testid = "digits", autoFocus, dark }) => {
  const refs = useRef([]);
  const set = (next, focus) => {
    const v = next.replace(/\D/g, "").slice(0, n);
    onChange(v);
    refs.current[Math.min(focus ?? v.length, n - 1)]?.focus();
    if (v.length === n) onComplete?.(v);
  };
  const digits = Array.from({ length: n }, (_, i) => value[i] || "");
  return (
    <div className="flex w-full gap-2" role="group" aria-label={label} data-testid={testid}>
      {digits.map((d, i) => (
        <input key={i} ref={(el) => { refs.current[i] = el; }} value={d} type={shown ? "text" : "password"} inputMode="numeric" pattern="[0-9]*" autoComplete="off" maxLength={n}
          autoFocus={autoFocus && i === 0} aria-label={`${label} digit ${i + 1}`} data-testid={`${testid}-${i}`}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, "");
            if (!typed) return;
            if (typed.length > 1) set(value.slice(0, i) + typed);
            else set(value.slice(0, i) + typed + value.slice(i + 1), i + 1);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              e.preventDefault();
              if (d) set(value.slice(0, i) + value.slice(i + 1), i);
              else if (i > 0) set(value.slice(0, i - 1) + value.slice(i), i - 1);
            } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            else if (e.key === "ArrowRight" && i < n - 1) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => { e.preventDefault(); set(e.clipboardData.getData("text")); }}
          className={cn("h-14 min-w-0 max-w-[60px] flex-1 rounded-xl border text-center text-2xl font-bold tabular-nums outline-none focus:ring-2 focus:ring-teal-400 sm:h-16",
            dark ? "border-white/20 bg-white/90 text-slate-900" : "border-slate-300 bg-white text-slate-900")} />
      ))}
    </div>
  );
};

/** Mirrors the server's weak-PIN rules for instant feedback. */
export const weakPin = (p) => {
  if (!/^\d{4}$/.test(p)) return "PIN must be exactly 4 digits";
  const d = [...p].map(Number);
  if (new Set(d).size === 1) return "Choose a PIN that isn't the same digit repeated";
  const steps = new Set([d[1] - d[0], d[2] - d[1], d[3] - d[2]]);
  if (steps.size === 1 && (steps.has(1) || steps.has(-1))) return "Choose a PIN that isn't a straight sequence like 1234 or 4321";
  if (/^(19|20)\d\d$/.test(p)) return "Choose a PIN that doesn't look like a year (19xx or 20xx)";
  return null;
};
export const weakPassword = (p) => (p.length < 8 || !/[A-Za-z]/.test(p) || !/\d/.test(p) ? "Use at least 8 characters with letters and numbers" : null);
